import {
  Box,
  Image,
  useDisclosure,
  Text,
  Progress,
  useBoolean,
  Button,
  Flex,
} from '@chakra-ui/react';
import BaseModal from 'components/Subscription/BaseModal';
import successImg from 'assets/success.png';
import failedImg from 'assets/failed.png';
import { useState } from 'react';
import { useQueryWrapper } from 'services/api/apiHelper';
import { useTenant } from 'context/TenantContext';
import { backToWebApp, convertParamsToString, formatCurrency, toTitleCase } from 'utils/helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { PUBLIC_PAGES } from 'routes/pagePath';
import { subRequest } from 'services/api/request';
import { PaymentStatusType, paymentResType } from 'utils/types';
import { resolvePaymentStatusReference } from 'utils/paymentReference';
import LanguageSwitcher from 'components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const PaymentVerification = () => {
  const { onClose } = useDisclosure();
  const location = useLocation();
  const reference = resolvePaymentStatusReference(location.search);
  const [isLoading, setIsLoading] = useBoolean(Boolean(reference));
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusType>('PENDING');
  const [successRes, setSuccessRes] = useState({
    amount: 0,
    currency: 'NGN',
    name: 'Bronze',
    status: paymentStatus,
    paymentPeriod: 12,
  });

  const [failedBody, setFailedBody] = useState({
    headerKey: 'somethingWrong' as 'somethingWrong' | 'failed',
    subTextKey: 'somethingWrongBody' as 'somethingWrongBody' | 'failedBody',
  });
  const url = reference
    ? convertParamsToString(subRequest.VERIFY_PAYMENT, { reference })
    : '';
  const onError = () => {
    setPaymentStatus('FAILED');
    setFailedBody({
      headerKey: 'somethingWrong',
      subTextKey: 'somethingWrongBody',
    });
    setIsLoading.off();
  };

  const onSuccess = (res: any) => {
    const data: paymentResType = res.data;
    const payStatus = data.status;
    switch (payStatus) {
      case 'SUCCESSFUL':
        setPaymentStatus('SUCCESSFUL');
        break;
      case 'FAILED':
        setPaymentStatus('FAILED');
        setFailedBody({
          headerKey: 'failed',
          subTextKey: 'failedBody',
        });
        break;
      default:
        setPaymentStatus('PENDING');
    }
    setSuccessRes(data);
    setIsLoading.off();
  };
  useQueryWrapper(['VERIFY_PAYMENT', reference], url, {
    onSuccess,
    onError,
    enabled: Boolean(reference),
  });

  return (
    <Box bg="rgba(0, 0, 0, 0.6)" minH="100vh">
      <Flex justifyContent="flex-end" p="16px">
        <LanguageSwitcher variant="dark" />
      </Flex>
      <BaseModal isOpen={true} onClose={onClose}>
        {!reference ? (
          <NotFoundBody />
        ) : isLoading ? (
          <LoadingBody />
        ) : paymentStatus === 'SUCCESSFUL' ? (
          <SuccessBody info={successRes} />
        ) : paymentStatus === 'FAILED' ? (
          <FailedBody failBody={failedBody} />
        ) : (
          <PendingBody />
        )}
      </BaseModal>
    </Box>
  );
};

export default PaymentVerification;

type LoadingBodyProps = {
  subText?: string;
};
export const LoadingBody = (props: LoadingBodyProps) => {
  const { t } = useTranslation('payment');
  const { subText } = props;
  return (
    <Box p="24px" textAlign="center">
      <Text fontWeight={500}>{t('processing')}</Text>
      <Text mt="1" mb="4" color="#666666">
        {subText ?? t('talkingToHq')}
      </Text>
      <Progress size="xs" isIndeterminate />
    </Box>
  );
};

type SuccessBodyProps = {
  info: paymentResType;
};
const SuccessBody = (props: SuccessBodyProps) => {
  const { t, i18n } = useTranslation(['payment', 'common']);
  const { tenant } = useTenant();
  const { info } = props;
  const { currency, amount, name, paymentPeriod } = info;
  const planName = toTitleCase(name);
  const [isLoading, setIsLoading] = useBoolean();

  return (
    <Box p="24px 31px" textAlign="center">
      <Text fontWeight={600} fontSize="18px">
        {t('payment:successful')}
      </Text>
      <Image mt="4" mx="auto" objectFit="cover" src={successImg} alt={t('payment:successAlt')} />
      <Text mt="20px" mb="28px">
        {t('payment:welcome', {
          tenantName: tenant.name,
          planName,
          amount: formatCurrency(amount, currency, i18n.language),
          months: paymentPeriod,
        })}
      </Text>
      <Button
        isLoading={isLoading}
        w="100%"
        h="48px"
        bg="primary"
        color="#fff"
        onClick={() => {
          setIsLoading.on();
          backToWebApp(tenant);
        }}
      >
        {t('common:backToApp', { tenantName: tenant.name })}
      </Button>
    </Box>
  );
};

type FailedBodyProps = {
  failBody: {
    headerKey: 'somethingWrong' | 'failed';
    subTextKey: 'somethingWrongBody' | 'failedBody';
  };
};
const FailedBody = (props: FailedBodyProps) => {
  const { t } = useTranslation('payment');
  const { failBody } = props;
  const { headerKey, subTextKey } = failBody;
  return (
    <Box p="24px 31px" textAlign="center">
      <Image
        w="48px"
        h="48px"
        mt="4"
        mx="auto"
        objectFit="cover"
        src={failedImg}
        alt={t('successAlt')}
      />
      <Text mt="4" fontWeight={600} fontSize="18px" color="#C71313">
        {t(headerKey)}
      </Text>

      <Text mt="4" mb="5">
        {t(subTextKey)}
      </Text>
    </Box>
  );
};

const NotFoundBody = () => {
  const { t } = useTranslation('payment');
  const navigate = useNavigate();

  return (
    <Box p="24px 31px" textAlign="center">
      <Image
        w="48px"
        h="48px"
        mt="4"
        mx="auto"
        objectFit="cover"
        src={failedImg}
        alt={t('successAlt')}
      />
      <Text mt="4" fontWeight={600} fontSize="18px" color="#C71313">
        {t('notFound')}
      </Text>
      <Text mt="4" mb="5">
        {t('notFoundBody')}
      </Text>
      <Button
        w="100%"
        h="48px"
        bg="primary"
        color="#fff"
        onClick={() => navigate(PUBLIC_PAGES.SUBSCRIPTION)}
      >
        {t('tryAgain')}
      </Button>
    </Box>
  );
};

const PendingBody = () => {
  const { t } = useTranslation('payment');
  return (
    <Box p="24px 31px" textAlign="center">
      <Text fontWeight={600} fontSize="18px">
        {t('pending')}
      </Text>

      <Text mt="20px" mb="28px">
        {t('pendingBody')}
      </Text>
    </Box>
  );
};
